import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { X, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { useQuotePopup } from "../context/QuotePopupContext";
import { createWholesaleEnquiry } from "../lib/leads";

const API = ""; // unused — kept for diff clarity; Firestore used instead

const PRODUCT_OPTIONS = [
  "Green Cardamom",
  "Almonds",
  "Cashews",
  "Pistachios",
  "Raisins",
  "Dates",
  "Multiple Products",
];

const QUANTITY_OPTIONS = [
  "Below 10 KG",
  "10 KG - 50 KG",
  "50 KG - 100 KG",
  "100 KG - 500 KG",
  "500 KG+",
];

const initial = {
  name: "",
  phone: "",
  email: "",
  product: "",
  quantity: "",
  additional_requirements: "",
};

export const WholesaleQuotePopup = () => {
  const { open, closePopup } = useQuotePopup();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) {
      // small delay so close animation can complete
      const t = setTimeout(() => {
        setSuccess(false);
        setErrors({});
        setForm(initial);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  const onChange = (k) => (eOrVal) => {
    const v = typeof eOrVal === "string" ? eOrVal : eOrVal.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Enter your full name";
    const mob = form.phone.replace(/\s|-/g, "");
    if (!/^[6-9]\d{9}$|^\+?\d{10,15}$/.test(mob))
      e.phone = "Enter a valid phone number";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.product) e.product = "Select a product";
    if (!form.quantity) e.quantity = "Select a quantity range";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.additional_requirements) delete payload.additional_requirements;
      await createWholesaleEnquiry(payload);
      setSuccess(true);
      toast.success(
        "Thank you for contacting ELA Traders. Our team will reach you shortly."
      );
    } catch (err) {
      toast.error(
        err?.message ||
          "Could not submit. Please try again or call us directly."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && closePopup()}>
      <DialogContent
        className="max-w-3xl p-0 overflow-hidden border-0 bg-transparent shadow-none [&>button]:hidden"
        data-testid="wholesale-quote-popup"
      >
        <DialogTitle className="sr-only">Get Today's Wholesale Price</DialogTitle>
        <DialogDescription className="sr-only">
          Premium enquiry form to receive bulk cardamom and dry fruit pricing
          from ELA Traders.
        </DialogDescription>

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.4 }}
              className="relative bg-[#0E4D3D] text-white p-10 md:p-16 text-center overflow-hidden"
              data-testid="wholesale-quote-success"
            >
              <div
                className="absolute inset-0 opacity-15 pointer-events-none"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 30% 20%, #C9A227 0, transparent 45%), radial-gradient(circle at 80% 80%, #C9A227 0, transparent 50%)",
                }}
              />
              <button
                onClick={closePopup}
                className="absolute top-4 right-4 text-white/70 hover:text-[#C9A227] transition-colors"
                data-testid="wholesale-quote-success-close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-[#C9A227]/15 border border-[#C9A227]/40 mx-auto flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-[#C9A227]" strokeWidth={1.4} />
                </div>
                <h3 className="mt-6 font-serif text-3xl md:text-4xl text-white">
                  Thank you for contacting{" "}
                  <span className="italic text-[#C9A227]">ELA Traders.</span>
                </h3>
                <p className="mt-5 text-white/80 max-w-xl mx-auto font-light leading-relaxed">
                  Our team will get in touch shortly with pricing and
                  availability details. Meanwhile, feel free to WhatsApp us for
                  an instant response.
                </p>
                <button
                  onClick={closePopup}
                  className="btn-gold mt-10"
                  data-testid="wholesale-quote-success-done"
                >
                  Continue Browsing
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="grid md:grid-cols-12 bg-[#FAF8F2] overflow-hidden max-h-[90vh] md:max-h-none overflow-y-auto"
            >
              {/* Side panel */}
              <div className="md:col-span-5 bg-[#0E4D3D] text-white p-7 md:p-10 relative overflow-hidden">
                <div
                  className="absolute inset-0 opacity-10 pointer-events-none"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 80% 20%, #C9A227 0, transparent 50%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#C9A227]" />
                    <span className="label-overline text-[#C9A227]">
                      Live Wholesale Rates
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl mt-4 leading-tight">
                    Get Today's Wholesale Price
                  </h3>
                  <p className="mt-4 text-white/75 text-sm font-light leading-relaxed">
                    Connect directly with ELA Traders for bulk cardamom and dry
                    fruit pricing.
                  </p>

                  <div className="mt-10 space-y-3 text-white/75 text-sm font-light">
                    {[
                      "Estate-direct Kerala sourcing",
                      "Response within 60 minutes",
                      "Samples available, no commitment",
                      "GST invoicing & pan-Karnataka delivery",
                    ].map((p) => (
                      <div key={p} className="flex items-start gap-2">
                        <CheckCircle2
                          className="w-4 h-4 text-[#C9A227] mt-0.5 shrink-0"
                          strokeWidth={1.4}
                        />
                        <span>{p}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-10 pt-6 border-t border-white/15">
                    <div className="label-overline text-white/50">
                      Direct trade line
                    </div>
                    <div className="font-serif text-[#C9A227] text-lg mt-1">
                      +91 77952 75838
                    </div>
                  </div>
                </div>
              </div>

              {/* Form panel */}
              <div className="md:col-span-7 p-7 md:p-10 relative">
                <button
                  onClick={closePopup}
                  className="absolute top-4 right-4 text-[#0E4D3D]/60 hover:text-[#C9A227] transition-colors z-10"
                  data-testid="wholesale-quote-close"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>

                <form
                  onSubmit={submit}
                  data-testid="wholesale-quote-form"
                  className="space-y-5"
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="label-overline text-[#0E4D3D]">
                        Full Name *
                      </label>
                      <input
                        value={form.name}
                        onChange={onChange("name")}
                        placeholder="Your full name"
                        className="input-luxury mt-1"
                        data-testid="wq-name"
                      />
                      {errors.name && (
                        <p
                          className="text-xs text-red-700 mt-1"
                          data-testid="wq-name-error"
                        >
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="label-overline text-[#0E4D3D]">
                        Phone Number *
                      </label>
                      <input
                        value={form.phone}
                        onChange={onChange("phone")}
                        placeholder="+91 98xxx xxxxx"
                        className="input-luxury mt-1"
                        data-testid="wq-phone"
                      />
                      {errors.phone && (
                        <p
                          className="text-xs text-red-700 mt-1"
                          data-testid="wq-phone-error"
                        >
                          {errors.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="label-overline text-[#0E4D3D]">
                      Email Address
                    </label>
                    <input
                      value={form.email}
                      onChange={onChange("email")}
                      placeholder="business@email.com (optional)"
                      className="input-luxury mt-1"
                      data-testid="wq-email"
                    />
                    {errors.email && (
                      <p
                        className="text-xs text-red-700 mt-1"
                        data-testid="wq-email-error"
                      >
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="label-overline text-[#0E4D3D]">
                        Product Interested In *
                      </label>
                      <Select
                        value={form.product}
                        onValueChange={onChange("product")}
                      >
                        <SelectTrigger
                          data-testid="wq-product"
                          className="mt-1 bg-transparent border-0 border-b border-[#0E4D3D]/25 rounded-none px-1 h-auto py-3 focus:ring-0 focus:border-[#C9A227] font-light"
                        >
                          <SelectValue placeholder="Select a product" />
                        </SelectTrigger>
                        <SelectContent>
                          {PRODUCT_OPTIONS.map((p) => (
                            <SelectItem
                              key={p}
                              value={p}
                              data-testid={`wq-product-${p.replace(/\W/g, "")}`}
                            >
                              {p}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.product && (
                        <p
                          className="text-xs text-red-700 mt-1"
                          data-testid="wq-product-error"
                        >
                          {errors.product}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="label-overline text-[#0E4D3D]">
                        Quantity Requirement *
                      </label>
                      <Select
                        value={form.quantity}
                        onValueChange={onChange("quantity")}
                      >
                        <SelectTrigger
                          data-testid="wq-quantity"
                          className="mt-1 bg-transparent border-0 border-b border-[#0E4D3D]/25 rounded-none px-1 h-auto py-3 focus:ring-0 focus:border-[#C9A227] font-light"
                        >
                          <SelectValue placeholder="Select quantity range" />
                        </SelectTrigger>
                        <SelectContent>
                          {QUANTITY_OPTIONS.map((q) => (
                            <SelectItem
                              key={q}
                              value={q}
                              data-testid={`wq-quantity-${q.replace(/\W/g, "")}`}
                            >
                              {q}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.quantity && (
                        <p
                          className="text-xs text-red-700 mt-1"
                          data-testid="wq-quantity-error"
                        >
                          {errors.quantity}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="label-overline text-[#0E4D3D]">
                      Additional Requirements
                    </label>
                    <textarea
                      value={form.additional_requirements}
                      onChange={onChange("additional_requirements")}
                      placeholder="Packaging, delivery timeline, certifications, etc."
                      rows={2}
                      className="input-luxury mt-1 resize-none"
                      data-testid="wq-additional"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-gold w-full justify-center mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="wq-submit"
                  >
                    {loading ? "Sending..." : "Request Wholesale Quote"}
                    <ArrowRight className="w-4 h-4" />
                  </button>

                  <p className="text-[10px] text-[#4A4A4A]/70 text-center font-light leading-relaxed pt-1">
                    By submitting, you consent to be contacted by ELA Traders
                    regarding wholesale enquiries. No spam — ever.
                  </p>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
